/* ──────────────────────────────────────────────
   ELECTRONICS ATTRIBUTES (FACETS)
   ID RANGE: 30001xxxx
────────────────────────────────────────────── */

import React from "react";
import { Ruler, Palette } from "lucide-react";

/* ================= SHARED TYPES ================= */

export interface ElectronicsAttributeItem {
  id: number;
  name: string;
  icon?: React.ReactNode;
  hexCodes?: string[];
  gradient?: string;
  numericValue?: number;
  unit?: string;
}

export interface ElectronicsAttributeGroup {
  id: number;
  name: string;
  appliesTo?: number[];
  values: ElectronicsAttributeItem[];
}

/* ======================================================
   📏 GLOBAL SIZE (LIKE FASHION)
   ====================================================== */

export const electronicsSizeAttributes: ElectronicsAttributeGroup = {
  id: 30000,
  name: "Size",
  values: [
    { id: 30000001, name: '32"', icon: <Ruler size={14} />, numericValue: 32, unit: "inch" },
    { id: 30000002, name: '40"', icon: <Ruler size={14} />, numericValue: 40, unit: "inch" },
    { id: 30000003, name: '43"', icon: <Ruler size={14} />, numericValue: 43, unit: "inch" },
    { id: 30000004, name: '50"', icon: <Ruler size={14} />, numericValue: 50, unit: "inch" },
    { id: 30000005, name: '55"', icon: <Ruler size={14} />, numericValue: 55, unit: "inch" },
    { id: 30000006, name: '65"', icon: <Ruler size={14} />, numericValue: 65, unit: "inch" },
    { id: 30000007, name: '75"+', icon: <Ruler size={14} />, numericValue: 75, unit: "inch" },
    { id: 30000020, name: "6.1 inch", icon: <Ruler size={14} />, numericValue: 6.1, unit: "inch" },
    { id: 30000021, name: "6.5 inch", icon: <Ruler size={14} />, numericValue: 6.5, unit: "inch" },
    { id: 30000022, name: "6.7 inch", icon: <Ruler size={14} />, numericValue: 6.7, unit: "inch" },
    { id: 30000023, name: "6.9 inch", icon: <Ruler size={14} />, numericValue: 6.9, unit: "inch" },
    
  ],
};

/* ======================================================
   🎨 GLOBAL COLOR (LIKE FASHION)
   ====================================================== */

export const electronicsColorAttributes: ElectronicsAttributeGroup = {
  id: 30001,
  name: "Color",
  values: [
    { id: 30001001, name: "Black", icon: <Palette size={14} />, hexCodes: ["#000000"] },
    { id: 30001002, name: "White", icon: <Palette size={14} />, hexCodes: ["#FFFFFF"] },
    { id: 30001003, name: "Silver", icon: <Palette size={14} />, hexCodes: ["#C0C0C0"] },
    { id: 30001004, name: "Gray", icon: <Palette size={14} />, hexCodes: ["#6B7280"] },
    { id: 30001005, name: "Blue", icon: <Palette size={14} />, hexCodes: ["#3B82F6"] },
    { id: 30001006, name: "Red", icon: <Palette size={14} />, hexCodes: ["#DC2626"] },
    { id: 30001007, name: "Gold", icon: <Palette size={14} />, hexCodes: ["#D4AF37"] },
    { id: 30001008, name: "Rose Gold", icon: <Palette size={14} />, hexCodes: ["#B76E79"] },

    {
      id: 30001009,
      name: "Gradient",
      icon: <Palette size={14} />,
      hexCodes: ["#3B82F6", "#9333EA"],
      gradient: "linear-gradient(135deg, #3B82F6, #9333EA)",
    },
  ],
};

/* ======================================================
   📺 TV ATTRIBUTES (subcategoryId: 301)
   ====================================================== */

export const tvAttributes: ElectronicsAttributeGroup[] = [
  {
    id: 30001010,
    name: "Resolution",
    appliesTo: [301],
    values: [
      { id: 300010201, name: "HD" },
      { id: 300010202, name: "Full HD" },
      { id: 300010203, name: "4K UHD" },
      { id: 300010204, name: "8K" },
      { id: 300010205, name: "16K" },
      { id: 300010206, name: "32K" },

    ],
  },
  {
    id: 30001011,
    name: "Display Type",
    appliesTo: [301],
    values: [
      { id: 300010301, name: "LED" },
      { id: 300010302, name: "OLED" },
      { id: 300010303, name: "QLED" },
      { id: 300010304, name: "Mini LED" },
      { id: 300010305, name: "Micro LED" },
      { id: 300010306, name: "Dual LED" },
      { id: 300010307, name: "Triple LED" },
      { id: 300010308, name: "Quad LED" },
      { id: 300010309, name: "P-OLED" },
      { id: 300010310, name: "S-OLED" },
      { id: 300010311, name: "Q-OLED" },

    ],
  },
  {
    id: 30001012,
    name: "Smart TV",
    appliesTo: [301],
    values: [
      { id: 300010401, name: "Yes" },
      { id: 300010402, name: "No" },
    ],
  },
];

/* ======================================================
   📱 PHONE ATTRIBUTES (subcategoryId: 101)
   ====================================================== */

export const phoneAttributes: ElectronicsAttributeGroup[] = [
  {
    id: 30003001,
    name: "Storage",
    appliesTo: [101],
    values: [
      { id: 300030101, name: "32 GB" },
      { id: 300030102, name: "64 GB" },
      { id: 300030103, name: "128 GB" },
      { id: 300030104, name: "256 GB" },
      { id: 300030105, name: "512 GB" },
      { id: 300030106, name: "1 TB" },



    ],
  },
  {
    id: 30003002,
    name: "RAM",
    appliesTo: [101],
    values: [
      { id: 300030201, name: "2 GB" },
      { id: 300030202, name: "4 GB" },
      { id: 300030203, name: "6 GB" },
      { id: 300030204, name: "8 GB" },
      { id: 300030205, name: "12 GB" },
      { id: 300030206, name: "16 GB" },
      { id: 300030207, name: "32 GB" },
            { id: 300030208, name: "64 GB" },
            { id: 300030209, name: "128 GB" },
            { id: 300030210, name: "256 GB" },
            { id: 300030211, name: "512 GB" },



    ],
  },
];

/* ======================================================
   🌐 COMMON ELECTRONICS ATTRIBUTES
   ====================================================== */

export const commonElectronicsAttributes: ElectronicsAttributeGroup[] = [
  {
    id: 30009001,
    name: "Brand",
    values: [
      { id: 300090101, name: "Samsung" },
      { id: 300090102, name: "LG" },
      { id: 300090103, name: "Sony" },
      { id: 300090104, name: "Apple" },
      { id: 300090105, name: "HP" },
      { id: 300090106, name: "Dell" },
      { id: 300090107, name: "Xiaomi" },
      { id: 300090108, name: "OnePlus" },
      { id: 300090109, name: "Google" },
      { id: 300090110, name: "Nokia" },
      { id: 300090111, name: "Motorola" },
      { id: 300090112, name: "Asus" },
      { id: 300090113, name: "Lenovo" },
      { id: 300090114, name: "Microsoft" },
      { id: 300090115, name: "Razer" },
      { id: 300090116, name: "Toshiba" },
      { id: 300090117, name: "Panasonic" },


    ],
  },
  {
    id: 30009002,
    name: "Warranty",
    values: [
      { id: 300090201, name: "No Warranty" },
      { id: 300090202, name: "6 Months" },
      { id: 300090203, name: "1 Year" },
      { id: 300090204, name: "2 Years" },
      { id: 300090205, name: "3 Years" },

    ],
  },
  {
    id: 30009003,
    name: "Condition",
    values: [
      { id: 300090301, name: "New" },
      { id: 300090302, name: "Refurbished" },
      { id: 300090303, name: "Used" },
    ],
  },
];

/* ======================================================
   📦 FINAL EXPORT
   ====================================================== */

export const electronicsAttributes: ElectronicsAttributeGroup[] = [
  electronicsSizeAttributes,
  electronicsColorAttributes,
  ...tvAttributes,
  ...phoneAttributes,
  ...commonElectronicsAttributes,
];